import React, {useCallback, useState} from 'react';

import {Button, Icon} from '@albert-io/atomic';

import {Explanation, SampleResponse} from '../shared/ContentBlocks';
import type {QuestionComponentProps} from '../shared/QuestionComponent/QuestionComponent.react';
import {QuestionComponent} from '../shared/QuestionComponent/QuestionComponent.react';
import RubricPreview from '../shared/RubricPreview/RubricPreview';

import type {FrqGrading} from './frqGradingStatus';
import IncompletenessBanner from './IncompletenessBanner';
import SubmittedFrqStatus from './SubmittedFrqStatus.react';
import ErrorBoundary from './WrittenSubmission/ErrorBoundary';
import WrittenSubmission from './WrittenSubmission/WrittenSubmission.react';

type FreeResponseQuestionComponentProps = QuestionComponentProps & {
  /**
   * In the teacher grading layout the sample response is shown in the grading
   * panel, so it's collapsed behind a toggle here to avoid duplication.
   */
  collapseSampleResponse?: boolean;
  /** Set where the response belongs to someone else — a teacher reading a student's submission. */
  isViewingStudentWork?: boolean;
  /**
   * The viewed guess's grading state, where a teacher-gated read supplies one. Absent in
   * independent practice and on a student's own view, which have no grading to report.
   */
  frqGrading?: FrqGrading;
};

const FreeResponseQuestionComponent = (props: FreeResponseQuestionComponentProps) => {
  // QuestionComponentProps declares none of these, so they must not reach the spread below.
  const {collapseSampleResponse, isViewingStudentWork, frqGrading, ...questionComponentProps} =
    props;
  const {activeQuestion, activeQuestionStore, guess, questionState} = questionComponentProps;
  const storeName = activeQuestionStore?.getName();

  // Track expansion by question id (not a bare boolean) because this component
  // instance persists across FRQ→FRQ navigation; a boolean would leak the
  // expanded state into the next question.
  const [expandedForQuestionId, setExpandedForQuestionId] = useState<string | null>(null);
  const isSampleExpanded = expandedForQuestionId === activeQuestion?.getId?.();
  // The rubric the backend exposes to students once they've submitted — the same source the
  // unscored RubricPreview reads below.
  const questionRubric = activeQuestion?.getMeta?.().getQuestionRubric?.();
  const InputFragment = useCallback(() => {
    let writtenSubmissionFragment: React.ReactNode = null;
    if (questionState === 'PreGuess') {
      writtenSubmissionFragment = (
        <WrittenSubmission
          storeName={storeName}
          question={activeQuestion}
          submitted={!!guess?.getId()}
          isViewingStudentWork={isViewingStudentWork}
        />
      );
    } else if (guess?.getId()) {
      writtenSubmissionFragment = (
        <>
          <SubmittedFrqStatus
            questionId={activeQuestion?.getId?.()}
            rubric={questionRubric}
            grading={frqGrading}
            isViewingStudentWork={isViewingStudentWork}
          />
          <WrittenSubmission
            storeName={storeName}
            question={activeQuestion}
            submitted
            isViewingStudentWork={isViewingStudentWork}
          />
        </>
      );
    } else if (!guess?.getId()) {
      writtenSubmissionFragment = <IncompletenessBanner />;
    }

    return questionState !== 'Dennis' ? (
      <ErrorBoundary>{writtenSubmissionFragment}</ErrorBoundary>
    ) : null;
  }, [
    activeQuestion,
    storeName,
    guess,
    questionState,
    questionRubric,
    isViewingStudentWork,
    frqGrading
  ]);

  const sampleResponseContent = (
    <>
      <SampleResponse question={activeQuestion} />
      {/* The structured rubric replaces the free-text explanation, falling back to it when
          there's no criteria rubric. The rubric lives in answer-key meta (question_rubric),
          which the backend only populates after a submitted guess. */}
      <RubricPreview
        rubric={activeQuestion?.getMeta?.().getQuestionRubric?.()}
        fallback={<Explanation question={activeQuestion} />}
      />
    </>
  );

  // Must stay non-null when collapsed: QuestionComponent falls back to a plain
  // <Explanation/> when customExplanation is nullish.
  const customExplanation = collapseSampleResponse ? (
    <div className='gap-x1 flex w-full flex-col items-start'>
      <Button
        variant='text'
        color='secondary'
        size='s'
        aria-expanded={isSampleExpanded}
        onClick={() =>
          setExpandedForQuestionId(isSampleExpanded ? null : (activeQuestion?.getId?.() ?? null))
        }
      >
        {isSampleExpanded ? 'Hide Sample Response' : 'Show Sample Response'}
        <Icon className='ml-x1' icon={isSampleExpanded ? 'chevron-up' : 'chevron-down'} />
      </Button>
      {isSampleExpanded && sampleResponseContent}
    </div>
  ) : (
    sampleResponseContent
  );

  return (
    <QuestionComponent
      {...questionComponentProps}
      customExplanation={customExplanation}
      customHOA={<></>}
      customResultIndicator={<></>}
      ActiveQuestionInputArea={InputFragment}
    />
  );
};

type FreeResponseQuestionTypeProps = Omit<FreeResponseQuestionComponentProps, 'questionState'>;

const FreeResponseQuestion = {
  PreGuess: (props: FreeResponseQuestionTypeProps) => (
    <FreeResponseQuestionComponent {...props} questionState='PreGuess' />
  ),
  PostGuess: (props: FreeResponseQuestionTypeProps) => (
    <FreeResponseQuestionComponent {...props} questionState='PostGuess' />
  ),
  Dennis: (props: FreeResponseQuestionTypeProps) => (
    <FreeResponseQuestionComponent {...props} questionState='Dennis' />
  )
};

export {FreeResponseQuestion};
